import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { defaultLineProps } from "@hex-insights/charts";
import { CaseStyle, ClassNameProps, convertCase, formatInt, stringToLocalDate } from "@hex-insights/core";
import { AttendanceRecordCountStatistics } from "../../../../../../../Utilities";
import { AttendanceStatusUtilities } from "../../../../../../Utilities";

const defaultStatus = {
	indicatesAbsent: false,
	indicatesLateArrival: false,
	indicatesEarlyDeparture: false,
	indicatesExcused: false,
};

export type SchoolAttendanceRecordLineChartDatum = {
	date: string;
	present: number;
	absent: number;
	tardy: number;
	notTaken: number;
};

export type SchoolAttendanceRecordLineChartProps = {
	data: SchoolAttendanceRecordLineChartDatum[];
} & Partial<ClassNameProps>;

export function SchoolAttendanceRecordLineChart({ data, className }: SchoolAttendanceRecordLineChartProps) {
	return (
		<ResponsiveContainer width="99%" height="100%" className={className}>
			<LineChart key={(data.length === 0).toString()} data={data}>
				<XAxis dataKey="date" tickFormatter={formatDateTick} />
				<Tooltip isAnimationActive={false} labelFormatter={formatDateLabel} formatter={tooltipFormatter} />

				<Line
					{...defaultLineProps}
					dataKey="notTaken"
					stroke={AttendanceStatusUtilities.color(null)}
					strokeWidth={12}
				/>
				<Line
					{...defaultLineProps}
					dataKey="tardy"
					stroke={AttendanceStatusUtilities.color({ ...defaultStatus, indicatesLateArrival: true })}
					strokeWidth={12}
				/>
				<Line
					{...defaultLineProps}
					dataKey="absent"
					stroke={AttendanceStatusUtilities.color({ ...defaultStatus, indicatesAbsent: true })}
					strokeWidth={12}
				/>
				<Line
					{...defaultLineProps}
					dataKey="present"
					stroke={AttendanceStatusUtilities.color({ ...defaultStatus })}
					strokeWidth={12}
				/>
			</LineChart>
		</ResponsiveContainer>
	);
}

function formatDateTick(value: string) {
	if (value === "auto") {
		// TODO removed by version 2.1.14 of recharts
		return "";
	}
	try {
		const dateValue = stringToLocalDate(value, "day");
		return dateValue.toLocaleString(undefined, {
			year: dateValue.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
			month: "numeric",
			day: "2-digit",
		});
	} catch {
		return "";
	}
}

function formatDateLabel(value: string) {
	return stringToLocalDate(value, "day").toLocaleString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		weekday: "short",
	});
}

function tooltipFormatter(value: number, name: string): [string, string] {
	return [formatInt(value), convertCase(name, CaseStyle.Title)];
}

export function statisticsToLineChartData(
	statisticsSets:
		| Pick<
				AttendanceRecordCountStatistics,
				"date" | "present" | "absentTotal" | "lateArrivalTotal" | "earlyDepartureTotal" | "notTaken"
		  >[]
		| null
		| undefined,
) {
	if (!statisticsSets) {
		return [];
	}

	return statisticsSets.map((statistics) => ({
		date: statistics.date,
		present: statistics.present,
		absent: statistics.absentTotal,
		tardy: statistics.lateArrivalTotal + statistics.earlyDepartureTotal,
		notTaken: statistics.notTaken,
	}));
}
